import { useEffect, useState } from 'react';
import { cardImageUrl, slugToTitle } from '../lib/cardImage';
import { cardArtFamily, cardNumber, toRoman } from '../lib/cardArt';
import type { Theme } from '../lib/personas';
import MysticCardArt from './cardArt/MysticCardArt';
import MinimalCardArt from './cardArt/MinimalCardArt';
import MorandiCardArt from './cardArt/MorandiCardArt';
import CardBackDesign from './CardBackDesign';

interface TarotCardProps {
  slug: string;
  /**
   * Theme override。不傳就讀 <html data-theme>。
   * 在 Session 中我們已用 applyPersonaTheme() 設過 DOM，所以多數呼叫端不需傳。
   */
  theme?: Theme;
  /** 是否已翻開（false → 顯示牌背，加上 flip 動畫）。 */
  revealed?: boolean;
  /** 進場後延遲翻牌（ms），讓多張牌依序翻。 */
  delay?: number;
  /** 顯示位置標籤（past / present / future / etc.）。 */
  position?: string;
  /** 翻牌速度。儀式 Overlay 會用較短速度，避免拖太久。 */
  flipMs?: number;
  /** 翻轉時增加光暈。 */
  glow?: boolean;
  /** 小尺寸固定牌桌可以關掉牌名，避免吃掉聊天區高度。 */
  showTitle?: boolean;
  className?: string;
}

/**
 * V2 TarotCard — 三套 chrome（mystic / minimal / morandi）+ 3D flip。
 *
 * Theme 解析優先序：
 *   1. `theme` prop
 *   2. <html data-theme> 屬性
 *   3. fallback 'minimal'
 *
 * 圖片優先序：
 *   1. R2 真圖（VITE_R2_PUBLIC_URL 設了才會 try）
 *   2. SVG 抽象 art（按 cardArtFamily 對應到 6 個 motif）— 不再用 placeholder gradient
 */
export default function TarotCard({
  slug,
  theme: themeProp,
  revealed = true,
  delay = 0,
  position,
  flipMs = 520,
  glow = false,
  showTitle = true,
  className = '',
}: TarotCardProps) {
  const [flipped, setFlipped] = useState(revealed && delay === 0);
  const [imgError, setImgError] = useState(false);
  const [domTheme, setDomTheme] = useState<Theme | null>(null);

  // 進場 delay 後翻牌
  useEffect(() => {
    if (!revealed) {
      setFlipped(false);
      return;
    }
    if (delay === 0) {
      setFlipped(true);
      return;
    }
    const t = window.setTimeout(() => setFlipped(true), delay);
    return () => window.clearTimeout(t);
  }, [revealed, delay]);

  // 讀 DOM data-theme（沒給 prop 才用）
  useEffect(() => {
    if (themeProp) return;
    const read = () => {
      const v = document.documentElement.getAttribute('data-theme');
      if (v === 'mystic' || v === 'minimal' || v === 'morandi') {
        setDomTheme(v);
      } else {
        setDomTheme('minimal');
      }
    };
    read();
    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, [themeProp]);

  const theme: Theme = themeProp ?? domTheme ?? 'minimal';
  const imgUrl = cardImageUrl(slug, theme);
  const title = slugToTitle(slug);
  const family = cardArtFamily(slug);
  const num = cardNumber(slug);
  const hasRealImg = Boolean(imgUrl) && !imgError;

  return (
    <div
      className={['flex flex-col items-center gap-2', className].join(' ')}
      style={{ perspective: '1000px' }}
    >
      <div
        className="relative aspect-[3/5] w-full max-w-[180px]"
        style={{
          transformStyle: 'preserve-3d',
          transition: `transform ${flipMs}ms cubic-bezier(0.22, 0.8, 0.24, 1), filter ${flipMs}ms ease`,
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          filter: glow && flipped ? 'drop-shadow(0 0 20px var(--color-accent))' : 'none',
        }}
      >
        {/* === 牌背（theme-specific） === */}
        <CardBack theme={theme} flipped={flipped} />

        {/* === 牌面（theme-specific） === */}
        <div
          aria-hidden={!flipped}
          className="absolute inset-0 overflow-hidden"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          {hasRealImg ? (
            <div
              className="h-full w-full overflow-hidden"
              style={{
                borderRadius: 'var(--radius-card)',
                border: '1px solid var(--color-border-hi)',
                background: 'var(--color-surface)',
              }}
            >
              <img
                src={imgUrl ?? ''}
                alt={title}
                onError={() => setImgError(true)}
                className="h-full w-full object-cover"
                loading={revealed ? 'eager' : 'lazy'}
                decoding="async"
              />
            </div>
          ) : (
            <CardFront theme={theme} title={title} family={family} num={num} />
          )}
        </div>
      </div>
      {position && (
        <span
          className="text-[10px] uppercase tracking-[0.2em]"
          style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-mono)' }}
        >
          {position}
        </span>
      )}
      {showTitle && (
        <span
          className="text-center text-sm leading-tight"
          style={{
            fontFamily: 'var(--font-display)',
            color: 'var(--color-heading)',
            fontWeight: 500,
          }}
        >
          {title}
        </span>
      )}
    </div>
  );
}

/* === 牌背：每個 theme 一個 motif === */

function CardBack({ theme, flipped }: { theme: Theme; flipped: boolean }) {
  return (
    <div
      aria-hidden={flipped}
      className="absolute inset-0"
      style={{
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
      }}
    >
      <CardBackDesign theme={theme} />
    </div>
  );
}

/* === 牌面：每個 theme 一個 chrome（無真圖時的 SVG 抽象視覺） === */

function CardFront({
  theme,
  title,
  family,
  num,
}: {
  theme: Theme;
  title: string;
  family: ReturnType<typeof cardArtFamily>;
  num: number | null;
}) {
  if (theme === 'mystic') {
    return (
      <div
        className="absolute inset-0 flex flex-col"
        style={{
          background: 'linear-gradient(180deg, #1f1a2e 0%, #14101e 100%)',
          border: '1px solid rgba(201,162,74,0.55)',
          borderRadius: 'var(--radius-card)',
          padding: '8% 6%',
          boxShadow:
            '0 6px 24px rgba(201,162,74,0.14), inset 0 0 0 1px rgba(201,162,74,0.1)',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: '11px',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#c9a24a',
            textAlign: 'center',
            fontWeight: 500,
          }}
        >
          {num !== null ? toRoman(num) : ''}
        </div>
        <div className="flex flex-1 items-center justify-center" style={{ padding: '6% 0' }}>
          <MysticCardArt family={family} />
        </div>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: '11px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#e8dcb9',
            textAlign: 'center',
            fontWeight: 500,
            lineHeight: 1.15,
          }}
        >
          {title.replace(/^The /, '')}
        </div>
      </div>
    );
  }
  if (theme === 'morandi') {
    return (
      <div
        className="absolute inset-0 flex flex-col"
        style={{
          background: '#fff',
          borderRadius: 'var(--radius-card)',
          padding: '10% 7%',
          boxShadow: '0 4px 16px rgba(138,122,111,0.18)',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: '10px',
            color: '#b87a6f',
            textAlign: 'center',
            letterSpacing: '0.18em',
            fontWeight: 600,
            textTransform: 'uppercase',
          }}
        >
          {num !== null ? String(num).padStart(2, '0') : ''}
        </div>
        <div className="flex flex-1 items-center justify-center" style={{ padding: '4% 0' }}>
          <MorandiCardArt family={family} />
        </div>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: '12px',
            color: '#3a302a',
            textAlign: 'center',
            lineHeight: 1.15,
            fontWeight: 500,
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </div>
      </div>
    );
  }
  // minimal
  return (
    <div
      className="absolute inset-0 flex flex-col"
      style={{
        background: '#fff',
        border: '1px solid rgba(10,10,10,0.2)',
        borderRadius: 'var(--radius-card)',
        padding: '9% 7%',
      }}
    >
      <div className="flex items-center justify-between">
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 9,
            color: '#6b6b68',
            letterSpacing: '0.15em',
          }}
        >
          {num !== null ? String(num).padStart(2, '0') : '—'}
        </span>
        <span
          style={{
            width: 5,
            height: 5,
            borderRadius: '50%',
            background: '#8a2a2a',
          }}
        />
      </div>
      <div className="flex flex-1 items-center justify-center">
        <MinimalCardArt family={family} />
      </div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 12,
          color: '#0a0a0a',
          textAlign: 'center',
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
        }}
      >
        {title}
      </div>
    </div>
  );
}
